/* eslint-disable unused-imports/no-unused-vars */
import { useState, useEffect, useCallback } from "react";
import { Button, Modal } from "react-bootstrap";

import { addClickRecord, formatDate, getToken } from "../../../Utils/helper";
import api, { api_without_cancellation } from "../../../api/api";
import GenrateDocument from "../../GenrateDocument/GenrateDocument";
import InfoPanelWithoutKeyValue from "../../ClientDashboard/shared/InfoPanelWithoutKeyValue";
import { useDocumentModal } from "../../common/CustomModal/CustomModalContext"; // Added for preview

import useClientPage from "./contact-panel/hooks/useClientPage";
import { useClientPanelData } from "./contact-panel/hooks/useClientPanelData";

const AddLetterPopup = ({ handleClose, show, caseId, clientId, refetch_data }) => {
  const [letterLog, setLetterLog] = useState({
    client_id: clientId,
    case_id: caseId,
    doc_id: null,
    description: "",
  });
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingDocs, setFetchingDocs] = useState(false);
  const [error, setError] = useState(null);
  const [showGenreateDocumentModal, setShowGenreateDocumentModal] = useState(false);
  const [instanceIdForGenrateDoc, setInstanceIdForGenragteDoc] = useState(null);
  const [dropdownName, setDropdownName] = useState("");

  const token = getToken();
  const { showDocumentModal } = useDocumentModal(); // Hook for doc preview
  const { clientData, refetch } = useClientPage(clientId, caseId);
  const panelData = useClientPanelData(clientData, formatDate);
  const [letterName, setLetterName] = useState("");

  // Helper to fetch documents
  const fetchDocs = useCallback(async () => {
    if (!clientId) return;
    setFetchingDocs(true);
    try {
      const response = await api.get(
        `/api/client-page/docs-from-client-page/get/${clientId}/${caseId}/`,
        {
          headers: { Authorization: token },
        }
      );
      setDocs(response.data || []);
    } catch (err) {
      console.error("Failed to fetch letter docs:", err);
      setDocs([]);
    } finally {
      setFetchingDocs(false);
    }
  }, [clientId, caseId, token]);

  // Initial Fetch
  useEffect(() => {
    if (show) {
      fetchDocs();
    }
  }, [show, fetchDocs]);

  // Listen for document completion from WordProcessor in new tab
  useEffect(() => {
    const handleStorageChange = async (e) => {
      if (e.key === "letterDocumentCompleted") {
        const data = JSON.parse(e.newValue);
        if (data) {
          // 1. Refetch docs to ensure the new doc is in the list
          await fetchDocs();

          // 2. Auto-select the new doc
          setLetterLog((prev) => ({
            ...prev,
            doc_id: data.doc_id,
            description: data.description || `Letter sent via ${data.file_name || "document"}`,
          }));
          setLetterName(data.file_name || "document");

          // Clean up
          localStorage.removeItem("letterDocumentCompleted");
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [fetchDocs]);

  const handleDocChange = (e) => {
    const docId = e.target.value ? parseInt(e.target.value, 10) : null;
    setLetterLog({
      ...letterLog,
      doc_id: docId,
    });
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      addClickRecord(999, 2);
      await api_without_cancellation.post(
        "/api/client-page/communication/letter-log/add/",
        {
          client_id: clientId,
          case_id: caseId,
          doc_id: null,
          description: letterLog.description,
          name: letterName,
        },
        { headers: { Authorization: token } }
      );

      if (refetch_data) {
        refetch_data();
        refetch();
      }
      handleClose();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create letter log");
    } finally {
      setLoading(false);
    }
  };

  // Identify the currently selected document object
  const selectedDoc = docs.find((d) => d.id === letterLog.doc_id);

  // Handle Document Preview
  const handleDocPreview = () => {
    if (selectedDoc) {
      // The upload URL is usually in 'upload' or 'file' depending on API structure
      // Adjust property access if your API returns the URL in a different field
      showDocumentModal("document", selectedDoc.upload || selectedDoc.file, selectedDoc);
    }
  };

  // Handle Deselection
  const handleDeselectDoc = () => {
    setLetterLog((prev) => ({ ...prev, doc_id: null }));
  };

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        aria-labelledby="chatModalLabel"
        centered
        dialogClassName="modal-dialog modal-dialog-centered dialog-letter-modal"
        style={{
          opacity: showGenreateDocumentModal ? "0" : "1",
        }}
      >
        <Modal.Header
          className="modal-header text-center p-0 bg-primary popup-heading-color justify-content-center"
          style={{
            borderBottom: "0px",
          }}
        >
          <h5 className="modal-title d-flex align-items-center justify-content-center font-size-20 height-25 font-weight-semibold text-uppercase popup-heading-color font-weight-500">
            Generate Client Letter
          </h5>
        </Modal.Header>
        <Modal.Body className="p-0 m-t-5 m-b-5">
          <div
            style={{
              minHeight: "200px",
              minWidth: "520px",
            }}
          >
            {/* Conditional Rendering: Show Doc Button OR Contact Panels */}
            {/* Dropdown - Always Visible */}
            <div
              style={{
                background: "var(--primary)",
                color: "white",
                fontSize: "14px",
                height: "25px",
                fontWeight: "600",
              }}
              className="d-flex m-b-5 align-items-center justify-content-center"
            >
              Add Letter Entry Without Generating a Letter.
            </div>
            <div className="row mx-auto align-items-center m-b-5 mb-3">
              <div className="col-md-2 p-l-5 pe-0 text-start">
                <span className="d-inline-block text-grey">Letter Name</span>
              </div>
              <div className="col-md-10 p-l-5 p-r-5">
                {/* <select
                  className="form-control p-l-5 height-25 py-0"
                  value={letterLog.doc_id || ""}
                  onChange={handleDocChange}
                  disabled={fetchingDocs}
                >
                  <option value="">{fetchingDocs ? "Loading docs..." : "Select Letter"}</option>
                  {docs?.map((doc) => (
                    <option key={doc.id} value={doc.id}>
                      {doc.file_name || `Letter ${doc.id}`}
                    </option>
                  ))}
                </select> */}
                <input
                  type="text"
                  value={letterName || ""}
                  placeholder="Letter Name"
                  onChange={(e) => setLetterName(e.target.value)}
                  className="form-control p-l-5 height-25 py-0"
                />
              </div>
            </div>

            <div className="row mx-auto align-items-start m-b-5 mb-3">
              <div className="col-md-2 p-l-5 pe-0 text-start">
                <span className="d-inline-block text-grey">Notes</span>
              </div>
              <div className="col-md-10 p-l-5 p-r-5">
                <textarea
                  rows="3"
                  value={letterLog.description}
                  onChange={(e) =>
                    setLetterLog({
                      ...letterLog,
                      description: e.target.value,
                    })
                  }
                  style={{
                    height: "80px",
                  }}
                  className="form-control textarea p-l-5 p-r-5 p-t-5 p-b-5"
                  placeholder="Letter Notes"
                />
              </div>
            </div>
            {selectedDoc ? (
              <div
                className="d-flex align-items-center justify-content-center m-b-15 m-t-10"
                style={{ gap: "10px", width: "100%" }}
              >
                {/* Document Button Preview */}
                <div
                  className="icon-text-boxes d-flex flex-wrap e-template-row height-25"
                  style={{ width: "fit-content" }}
                >
                  <div
                    className="col-12 icon-text-box text-center height-25 font-weight-semibold cursor-pointer btn-hover-document-row"
                    id="no-vertical-border"
                    style={{
                      borderColor: "var(--primary)",
                      backgroundColor: "var(--primary-10)",
                      color: "var(--primary)",
                      border: "1px solid var(--primary)",
                      maxWidth: "255px",
                    }}
                    onClick={() => {
                      addClickRecord(999, 2);
                      handleDocPreview();
                    }}
                  >
                    <p className="date m-0">
                      {selectedDoc.created
                        ? new Date(selectedDoc.created).toLocaleDateString()
                        : "-"}
                    </p>
                    <span
                      className="icon-wrap"
                      style={{
                        height: "15px",
                        width: "15px",
                        maxWidth: "15px",
                        flex: "0 0 15px",
                        margin: "0 5px",
                      }}
                    >
                      <i className="ic ic-15 ic-file-colored cursor-pointer" />
                    </span>
                    <p className="name m-0 text-truncate" style={{ maxWidth: "120px" }}>
                      {selectedDoc.file_name}
                    </p>
                  </div>
                </div>

                {/* Clear Selection Button */}
                <div
                  className="cursor-pointer text-danger fw-bold"
                  onClick={() => {
                    handleDeselectDoc();
                    addClickRecord(999, 2);
                  }}
                  title="Remove selection"
                  style={{ fontSize: "18px" }}
                >
                  ✕
                </div>
              </div>
            ) : (
              /* Contact Panels for generating new docs */

              <div className="d-flex flex-wrap p-r-5 p-l-5 p-b-5" style={{ gap: "5px", flex: 1 }}>
                <div className="min-w-260px-client">
                  <InfoPanelWithoutKeyValue
                    panel_name="Client Address 1"
                    data={panelData.clientAddress1}
                    defaultLength={3}
                    panelClassName="min-height-auto pe-0 info-panel-without-key-value"
                    hasBtn={true}
                    buttonText="Generate Document"
                    iconClassName="ic ic-19 ic-generate-document m-r-5"
                    onClick={() => {
                      addClickRecord(999, 2);
                      setDropdownName(1);
                      setInstanceIdForGenragteDoc(clientData.client?.contact_1?.id);
                      setShowGenreateDocumentModal(true);
                    }}
                    templateDropdownId={1}
                    templatePageId={2}
                    className={"mt-0"}
                  />
                </div>
                <div className="min-w-260px-client">
                  <InfoPanelWithoutKeyValue
                    panel_name="Client Address 2"
                    data={panelData.clientAddress2}
                    defaultLength={3}
                    panelClassName="min-height-auto pe-0 info-panel-without-key-value"
                    hasBtn={true}
                    buttonText="Generate Document"
                    iconClassName="ic ic-19 ic-generate-document m-r-5"
                    onClick={() => {
                      addClickRecord(999, 2);
                      setDropdownName(14);
                      setInstanceIdForGenragteDoc(clientData?.client?.contact_2?.id);
                      setShowGenreateDocumentModal(true);
                    }}
                    templateDropdownId={14}
                    templatePageId={2}
                    className={"mt-0"}
                  />
                </div>
                <div className="min-w-contact-blocks">
                  <InfoPanelWithoutKeyValue
                    panel_name="Client Spouse Contact"
                    data={panelData.clientSpouseContact}
                    defaultLength={3}
                    panelClassName="min-height-auto pe-0 info-panel-without-key-value"
                    hasBtn={true}
                    buttonText="Generate Document"
                    iconClassName="ic ic-19 ic-generate-document m-r-5"
                    onClick={() => {
                      addClickRecord(999, 2);
                      setDropdownName(24);
                      setInstanceIdForGenragteDoc(clientData.client?.spouse?.id);
                      setShowGenreateDocumentModal(true);
                    }}
                    templateDropdownId={24}
                    templatePageId={2}
                    className={"mt-0"}
                  />
                </div>
                <div className="min-w-contact-blocks">
                  <InfoPanelWithoutKeyValue
                    panel_name="Emergency Contact"
                    data={panelData?.clientEmergencyContact}
                    defaultLength={3}
                    panelClassName="min-height-auto pe-0 info-panel-without-key-value"
                    hasBtn={true}
                    buttonText="Generate Document"
                    iconClassName="ic ic-19 ic-generate-document m-r-5"
                    onClick={() => {
                      addClickRecord(999, 2);
                      setDropdownName(2);
                      setInstanceIdForGenragteDoc(clientData.client?.emergency_contact?.id);
                      setShowGenreateDocumentModal(true);
                    }}
                    templateDropdownId={2}
                    templatePageId={2}
                    className={"mt-0"}
                  />
                </div>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer
          className="p-0 mt-0 padding-outside-btn-new-provider"
          style={{ borderTop: "none" }}
        >
          <Button
            className="button-padding-footer-new-provider d-flex align-items-center justify-content-center"
            variant="secondary"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            className="button-padding-footer-new-provider d-flex align-items-center justify-content-center"
            onClick={handleSubmit}
            disabled={loading || fetchingDocs}
          >
            {loading ? "Adding..." : "Add Letter"}
          </Button>
        </Modal.Footer>
      </Modal>
      {showGenreateDocumentModal && instanceIdForGenrateDoc && (
        <GenrateDocument
          show={showGenreateDocumentModal}
          handleClose={() => {
            addClickRecord(999, 2);
            setDropdownName(null);
            setInstanceIdForGenragteDoc(null);
            setShowGenreateDocumentModal(false);
          }}
          dropdownName={dropdownName}
          PageEntity="Client"
          instanceId={instanceIdForGenrateDoc}
          openInNewTab={true}
          isFromLetterPopup={true}
        />
      )}
    </>
  );
};

export default AddLetterPopup;
